import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_US1_NETWORK_TMS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableUS1NetworkTMSAppInput = {
  appType: AppTypeEnum
  password: string
  tmsURL: string
  username: string
}

const formSchema = z.object({
  password: z.string().min(1, 'Password is required'),
  tmsURL: z.string().min(1, 'US1Network TMS URL is required'),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the US1Network TMS app.
 */
export function US1NetworkTMSModal({ isOpen, onClose, appType, app, isModalEditable }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      password: '',
      tmsURL: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  useEffect(() => {
    if (app?.workspaceApp?.appCredentials) {
      form.reset({
        password: app.workspaceApp.appCredentials.password || '',
        tmsURL: app.workspaceApp.appMetadata?.tmsURL || '',
        username: app.workspaceApp.appCredentials.username || ''
      })
    }
  }, [app, form])

  const [enableUS1NetworkTMSApp, { loading }] = useMutation<
    { enableUS1NetworkTMSApp: { password: string; tmsURL: string; username: string } },
    EnableUS1NetworkTMSAppInput
  >(ENABLE_US1_NETWORK_TMS_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('US1Network TMS app enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
      toast.error(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableUS1NetworkTMSApp({
      variables: {
        appType: appType as AppTypeEnum,
        password: values.password,
        tmsURL: values.tmsURL,
        username: values.username
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isModalEditable ? 'View US1Network TMS Credentials' : 'Enable US1Network TMS for Chain'}
          </DialogTitle>
          <DialogDescription>
            {isModalEditable
              ? 'View your US1Network TMS credentials below'
              : 'Enter your US1Network TMS credentials to enable the integration'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6 w-full">
            <FormField
              control={form.control}
              name="tmsURL"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Truckman API URL</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="i.e. https://truckman3.com"
                      {...field}
                      disabled={loading || isModalEditable}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Truckman API Username</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="i.e. ChainApp"
                      {...field}
                      disabled={loading || isModalEditable}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Truckman API Password</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      type="password"
                      placeholder="i.e. S0meStr0ngP@ssw0rd"
                      {...field}
                      disabled={loading || isModalEditable}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://help.trychain.com/en"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                Read the integration guide
                <ExternalLink className="h-4 w-4" />
              </a>

              {!isModalEditable && (
                <div className="space-x-2">
                  <Button variant="outline" onClick={handleClose} type="button">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                    {loading ? 'Enabling...' : app?.workspaceApp?.appCredentials ? 'Update' : 'Enable'}
                  </Button>
                </div>
              )}
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
